import { useCallback, useEffect } from "react";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";

type UseEditQuizInitProps = Pick<
	EditQuizPageProps,
	"enforcedProfilesOnly" | "url" | "proctoringAssessmentConfiguration" | "setProctoringAssessmentConfiguration"
> &
	Pick<UseEditQuizStateReturn, "live" | "profile" | "setting" | "student" | "theme">;

/**
 * Call init functions here.
 * Originally in editQuiz.ts
 */
export default function useEditQuizInit({
	enforcedProfilesOnly,
	url,
	proctoringAssessmentConfiguration,
	setProctoringAssessmentConfiguration,
	...props
}: UseEditQuizInitProps) {
	const { getAssessmentUuids, fetchInstituteData, fetchCourseData, fetchStaticData, setLoading } = props.setting.action;
	const { fetchAndSetLiveSessions } = props.live.action;
	const { fetchAndFilterProfiles, fetchInstituteProfilePermissions } = props.profile.action;
	const { fetchAndSetExemptionInfo } = props.student.action;
	const { initStandaloneExemptionInfo } = props.student.action;
	const { handleFetchAndSetTheme } = props.theme.action;

	const getEditAssessmentInfo = useCallback(async () => {
		try {
			setLoading(true);

			const { verityInstituteSettings } = await fetchStaticData();
			const { assessmentUUids, verityCourseId } = await getAssessmentUuids();
			await fetchInstituteData(assessmentUUids.instituteUuid);
			await fetchInstituteProfilePermissions();

			if (verityCourseId) {
				await fetchCourseData(verityCourseId);
			}

			const tempProfileMap = await fetchAndFilterProfiles(verityInstituteSettings);
			await fetchAndSetLiveSessions(assessmentUUids);
			await fetchAndSetExemptionInfo(tempProfileMap);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [
		setLoading,
		fetchStaticData,
		getAssessmentUuids,
		fetchInstituteData,
		fetchInstituteProfilePermissions,
		fetchAndFilterProfiles,
		fetchAndSetLiveSessions,
		fetchAndSetExemptionInfo,
		fetchCourseData,
	]);

	useEffect(() => {
		void getEditAssessmentInfo();
	}, [enforcedProfilesOnly, getEditAssessmentInfo]);

	// Populates thirdPartyUrl on mount if a url is passed to the parent
	useEffect(() => {
		if (
			url &&
			proctoringAssessmentConfiguration &&
			proctoringAssessmentConfiguration.thirdPartyAssessmentOption &&
			!proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.thirdPartyUrl
		) {
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: url,
				},
			});
		}
	}, [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration, url]);

	useEffect(() => {
		handleFetchAndSetTheme();
	}, [handleFetchAndSetTheme]);

	useEffect(() => {
		initStandaloneExemptionInfo();
	}, [initStandaloneExemptionInfo]);
}
